"use client";

import { useActionState } from "react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import Link from "next/link";
import { Logo } from "@/components/ui/Logo";
import { ArrowLeft } from "lucide-react";
import { sendPasswordResetLink } from "@/actions/auth"; // We will add this export later

export default function ForgotPasswordPage() {
    const [state, dispatch] = useActionState<string | undefined, FormData>(sendPasswordResetLink, undefined);

    return (
        <div className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden bg-slate-50">
            {/* Left Side - Form */}
            <div className="flex items-center justify-center p-8 relative z-10">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(#cbd5e1 1px, transparent 1px)',
                        backgroundSize: '20px 20px'
                    }} />
                </div>

                <div className="relative z-10 w-full max-w-md space-y-8">
                    {/* Logo */}
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <Logo size="md" className="scale-90 md:scale-100 origin-center" />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900">
                            Recuperar Contraseña
                        </h1>
                        <p className="text-slate-500 mt-2">
                            Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu acceso.
                        </p>
                    </div>

                    {/* Card */}
                    <div className="bg-white border border-slate-200 shadow-xl rounded-2xl p-8">
                        <form action={dispatch} className="space-y-5">
                            <div className="flex flex-col gap-4">
                                <Input
                                    label="Email"
                                    type="email"
                                    name="email"
                                    placeholder="nombre@ejemplo.com"
                                    required
                                    className="bg-slate-50 border-slate-200 focus:border-blue-900 focus:ring-blue-900/20"
                                />

                                <Button
                                    className="w-full bg-blue-900 hover:bg-blue-800 text-white shadow-lg shadow-blue-900/30 border-0 h-12 text-base font-semibold transition-all hover:shadow-xl"
                                    type="submit"
                                >
                                    Enviar enlace de recuperación
                                </Button>
                            </div>

                            {state && (
                                <div className={`p-4 rounded-lg text-sm font-medium ${state.includes("enlace") ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"
                                    }`}>
                                    {state}
                                </div>
                            )}
                        </form>

                        <div className="mt-6 pt-6 border-t border-slate-100 text-center">
                            <Link href="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-blue-900 transition-colors">
                                <ArrowLeft className="w-4 h-4" />
                                Volver a Iniciar Sesión
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right Side - Brand Panel (Same as Login) */}
            <div className="hidden lg:flex relative overflow-hidden bg-blue-900 items-center justify-center p-12">
                {/* Decorative Elements */}
                <div className="absolute inset-0">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-blue-800 rounded-full blur-3xl opacity-50 -translate-y-1/2 translate-x-1/2" />
                    <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-600 rounded-full blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2" />
                </div>

                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)',
                        backgroundSize: '30px 30px'
                    }} />
                </div>

                <div className="relative z-10 max-w-lg text-center space-y-12">
                    {/* Main Message */}
                    <div className="space-y-6">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full border border-white/20 backdrop-blur-sm">
                            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                            <span className="text-white/80 text-sm font-medium">Sistema DTE Certificado</span>
                        </div>

                        <h2 className="text-4xl font-bold text-white leading-tight">
                            Recupera tu acceso<br />
                            <span className="text-amber-400">fácilmente</span>
                        </h2>

                        <p className="text-lg text-blue-200 leading-relaxed max-w-md mx-auto">
                            Si tienes problemas, nuestro equipo de soporte está listo para ayudarte en cualquier momento.
                        </p>
                    </div>

                    {/* Trust Badges */}
                    <div className="flex items-center justify-center gap-6 pt-4">
                        <div className="text-center">
                            <p className="text-3xl font-bold text-white">24/7</p>
                            <p className="text-blue-300 text-sm">Soporte</p>
                        </div>
                        <div className="w-px h-12 bg-white/20" />
                        <div className="text-center">
                            <p className="text-3xl font-bold text-white">100%</p>
                            <p className="text-blue-300 text-sm">Seguro</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
